/**
 * Management Customers page — additive.
 * Aggregates customers from existing requests + enriches with editable
 * profile data via /api/customers. Auto-saves on field change. PDF export.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, Search, Loader2, Download, Phone, Mail, MapPin, Star,
  Briefcase, ChevronRight, CheckCircle2, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  active:   "bg-emerald-100 text-emerald-700 border-emerald-300",
  inactive: "bg-zinc-200 text-zinc-700 border-zinc-300",
  vip:      "bg-amber-100 text-amber-700 border-amber-300",
};
const CONTACT_METHODS = [
  { v: "phone", label: "Phone" }, { v: "sms",  label: "SMS" },
  { v: "email", label: "Email" }, { v: "any",   label: "Any" },
];

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [openKey, setOpenKey]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/customers", { params: { search } });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-3" data-testid="customers-page">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-red-600" /> Customers
          </h1>
          <p className="text-sm text-zinc-500">{customers.length} customer(s) · click a row to edit</p>
        </div>
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="p-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search name / phone / email"
                 className="border-0 focus-visible:ring-0"
                 data-testid="cust-search" />
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : (
        <Card className="border border-zinc-200">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase text-zinc-500 font-bold">
                <tr>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Email</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Address</th>
                  <th className="text-left px-3 py-2">Jobs</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Last Service</th>
                  <th className="text-right px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.customer_key} onClick={() => setOpenKey(c.customer_key)}
                      className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      data-testid={`cust-row-${c.customer_key}`}>
                    <td className="px-3 py-2 font-semibold text-zinc-900">{c.full_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-600 text-xs">{c.phone || "—"}</td>
                    <td className="px-3 py-2 text-zinc-600 text-xs hidden md:table-cell">{c.email || "—"}</td>
                    <td className="px-3 py-2 text-zinc-500 text-xs hidden lg:table-cell">{c.address || "—"}</td>
                    <td className="px-3 py-2 text-xs"><Badge className="bg-zinc-100 text-zinc-700">{c.total_jobs}</Badge></td>
                    <td className="px-3 py-2 text-xs text-zinc-500 hidden md:table-cell">
                      {c.last_service_date ? new Date(c.last_service_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_BADGE[c.customer_status] || STATUS_BADGE.active}`}>
                        {c.customer_status || "active"}
                      </span>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-zinc-400 italic py-8 text-xs">No customers match.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {openKey && (
        <CustomerDetail customerKey={openKey} onClose={() => { setOpenKey(null); load(); }} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function CustomerDetail({ customerKey, onClose }) {
  const [c, setC] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [det, vs] = await Promise.all([
        API.get(`/customers/${customerKey}`),
        API.get("/management/vendors?limit=200"),
      ]);
      setC(det.data.customer);
      setVendors(vs.data.vendors || []);
    } catch (e) {
      toast.error("Failed to load customer");
    }
  }, [customerKey]);
  useEffect(() => { load(); }, [load]);

  const autoSave = (patch) => {
    setC(prev => ({ ...prev, ...patch }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await API.patch(`/customers/${customerKey}`, patch);
        setC(data.customer);
        setLastSaved(new Date());
      } catch (e) {
        toast.error(e.response?.data?.detail || "Save failed");
      } finally { setSaving(false); }
    }, 700);
  };

  const toggleTech = (techId) => {
    const tagged = c.tagged_technicians || [];
    const next = tagged.includes(techId)
      ? tagged.filter(t => t !== techId)
      : [...tagged, techId];
    autoSave({ tagged_technicians: next });
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("apex_token") || "";
      const res = await fetch(`${baseURL}/api/customers/${customerKey}/export-pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `customer-${customerKey}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  if (!c) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cust-detail-panel">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-red-600" /> Customer Profile
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
            {!saving && lastSaved && (
              <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Auto-saves on every change · {c.total_jobs} job(s) · status: {c.customer_status}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Full Name</Label>
              <Input value={c.full_name || ""} onChange={e => autoSave({ full_name: e.target.value })}
                     data-testid="cust-name" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={c.phone_number || ""} onChange={e => autoSave({ phone_number: e.target.value })}
                     data-testid="cust-phone" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={c.email || ""} onChange={e => autoSave({ email: e.target.value })}
                     data-testid="cust-email" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Address</Label>
              <Input value={c.physical_address || ""} onChange={e => autoSave({ physical_address: e.target.value })}
                     data-testid="cust-address" />
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Customer Status</Label>
              <Select value={c.customer_status || "active"}
                      onValueChange={v => autoSave({ customer_status: v })}>
                <SelectTrigger data-testid="cust-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Preferred Contact</Label>
              <Select value={c.preferred_contact || "phone"}
                      onValueChange={v => autoSave({ preferred_contact: v })}>
                <SelectTrigger data-testid="cust-contact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map(m => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={c.notes || ""}
                      onChange={e => autoSave({ notes: e.target.value })}
                      placeholder="Standing instructions, allergies, gate codes, dog…"
                      data-testid="cust-notes" />
          </div>
          <div>
            <Label className="text-xs">Custom Notes</Label>
            <Textarea rows={2} value={c.custom_notes || ""}
                      onChange={e => autoSave({ custom_notes: e.target.value })}
                      placeholder="Anything else"
                      data-testid="cust-custom-notes" />
          </div>

          {/* Tagged Technicians */}
          <div>
            <Label className="text-xs">Tagged Technicians ({(c.tagged_technicians || []).length})</Label>
            <div className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto p-2 border border-zinc-200 rounded">
              {vendors.map(v => {
                const on = (c.tagged_technicians || []).includes(v.id);
                return (
                  <button key={v.id} type="button"
                          onClick={() => toggleTech(v.id)}
                          className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                            on ? "bg-red-600 border-red-600 text-white"
                               : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                          }`}
                          data-testid={`cust-tag-${v.id}`}>
                    {v.full_name || v.email}
                  </button>
                );
              })}
              {vendors.length === 0 && <p className="text-[10px] text-zinc-400 italic">No technicians available</p>}
            </div>
          </div>

          {/* Service History */}
          <div>
            <Label className="text-xs">Service History · {c.total_jobs} job(s)</Label>
            <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
              {(c.service_history || []).map(h => (
                <div key={h.id} className="flex items-center gap-2 p-2 border border-zinc-100 rounded text-xs">
                  <Briefcase className="w-3 h-3 text-zinc-400 flex-shrink-0" />
                  <span className="font-mono text-zinc-400">{h.request_code}</span>
                  <span className="flex-1 truncate text-zinc-700">{h.service_type}</span>
                  <Badge className="text-[9px]">{h.status}</Badge>
                  <span className="text-[10px] text-zinc-400">{h.created_at?.slice(0, 10)}</span>
                </div>
              ))}
              {(c.service_history || []).length === 0 && (
                <p className="text-[11px] text-zinc-400 italic">No previous jobs.</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2 pt-2 border-t border-zinc-200">
            <Button onClick={exportPDF} disabled={exporting} variant="outline"
                    className="flex-1 text-xs" data-testid="cust-export-pdf">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                         : <><Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF</>}
            </Button>
            <Button onClick={onClose} className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-xs"
                    data-testid="cust-close-btn">
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
